import { useMemo } from "react";

import { Button } from "primereact/button";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { SandboxTab } from "sj-ui-toolkit/src/client";

import { TabPanel, TabView } from "primereact/tabview";
import { useSandbox } from "../SandboxLesson/contexts/SandboxHooks";
import { useTabs } from "../SandboxLesson/contexts/Tabs";
import SandboxBrowser from "../SandboxLesson/RightPanel/Tabs/SandboxBrowser";

import S from "../SandboxLesson/_styles.module.scss";
import PostSubmissionModal from "../SandboxLesson/RightPanel/Dialogs/PostSubmissionModal";
import SandboxStatusLoader from "../SandboxLesson/RightPanel/Dialogs/SandboxStatusLoader";
import SessionExpiringOverlay from "../SandboxLesson/RightPanel/Dialogs/SessionExpiringOverlay";

export default function AppLabControls({ tabs }: { tabs?: SandboxTab[] }) {
  const { activeTabIndex, setActiveTab } = useTabs();
  const { impersonatedEmail } = useUserContext();
  const { resetSandbox, isTestRunning, runTests } = useSandbox();

  const isImpersonating = !!impersonatedEmail;
  const ctrlKeySeq = /Mac|Mac OS|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent) ? "⌘" : "Ctrl+";

  // filter out any non-app (proxy, editor, etc.) tabs and hardcode navigation to false
  const appTabs = useMemo(
    () =>
      tabs
        ?.filter(tab => tab.type === "app")
        .map(tab => ({ ...tab, app: { ...tab.app, navigation: false } })) || [],
    [tabs]
  );

  const handleRun = async () => {
    try {
      runTests();
    } catch (error) {
      console.error("Error running tests:", error);
    }
  };

  return (
    <div className="flex flex-col h-full gap-2 relative" data-testid="app-lab-controls">
      <SandboxStatusLoader />
      <SessionExpiringOverlay />

      <div className={`flex gap-2 ${S.sandboxHeader}`}>
        <Button
          icon="pi pi-play"
          label="Run tests"
          disabled={isTestRunning || isImpersonating}
          loading={isTestRunning}
          size="small"
          data-testid="app-lab-run-tests-button"
          onClick={handleRun}
        >
          <span className="button-text-muted ml-1">{`${ctrlKeySeq}R`}</span>
        </Button>

        <Button
          icon="pi pi-refresh"
          outlined
          severity="secondary"
          text
          size="small"
          onClick={() => resetSandbox()}
          className="ml-auto"
          label="Reset lesson"
          data-testid="app-lab-reset-button"
          disabled={isImpersonating}
        />
      </div>
      <div className={`flex-2 ${S.sandboxContent}`}>
        {appTabs.length === 1 && <SandboxBrowser {...appTabs[0]!} />}
        {appTabs.length > 1 && (
          <TabView
            aria-label="Sandbox environment"
            activeIndex={activeTabIndex}
            onTabChange={e => setActiveTab(e.index)}
            renderActiveOnly={false}
            className="h-full"
            data-testid="app-lab-tabs"
            pt={{
              root: { className: "h-full" },
              panelContainer: { className: S.tabContent },
            }}
          >
            {appTabs.map(tab => (
              <TabPanel key={tab.title} header={tab.title} contentClassName="h-full">
                <SandboxBrowser {...tab} />
              </TabPanel>
            ))}
          </TabView>
        )}
      </div>
      <PostSubmissionModal />
    </div>
  );
}
